import { Request } from 'express';
import { getCookieRaw } from './cookieUtils';

export const TRUST_ARC_PREFERENCE_COOKIE_NAME = 'cmapi_cookie_privacy';
export const TRUST_ARC_LOCALE = 'notice_behavior';

export enum UserConsentLevel {
  REQUIRED = 'REQUIRED',
  FUNCTIONAL = 'FUNCTIONAL',
  ADVERTISING = 'ADVERTISING',
  UNKNOWN = 'UNKNOWN',
}

// Geographic areas under which cookie laws of interest appear (E.g., CCPA = California Consumer Privacy Act)
export const enum ConsentLocale {
  CCPA = 'CCPA',
  GDPR = 'GDPR',
  NONE = 'NONE',
  UNKNOWN = 'UNKNOWN',
}

// Gets user's geographic locale based on notice_behavior cookie
export const getConsentLocale = (trustArcLocaleCookie: string): ConsentLocale => {
  if (!trustArcLocaleCookie) {
    return ConsentLocale.UNKNOWN;
  }

  if (trustArcLocaleCookie.includes('us')) {
    return ConsentLocale.CCPA;
  } else if (trustArcLocaleCookie.includes('eu')) {
    return ConsentLocale.GDPR;
  } else if (trustArcLocaleCookie.includes('none')) {
    return ConsentLocale.NONE;
  } else {
    return ConsentLocale.UNKNOWN;
  }
};

// Reads the cmapi_cookie_privacy cookie and returns the users desired level of tracking
const getConsentPreferenceFromCookie = (
  cookieValue: string | null | undefined
): UserConsentLevel[] => {
  const FUNCTIONAL = '2';
  const ADVERTISING = '3';
  const preferences: UserConsentLevel[] = [UserConsentLevel.REQUIRED];

  if (!cookieValue) {
    return [UserConsentLevel.UNKNOWN];
  }

  if (cookieValue.includes(FUNCTIONAL)) {
    preferences.push(UserConsentLevel.FUNCTIONAL);
  }
  if (cookieValue.includes(ADVERTISING)) {
    preferences.push(UserConsentLevel.ADVERTISING);
  }

  return preferences;
};

// Get user consent level based on cmapi_cookie_privacy and user locale (notice_behavior cookie)
const getConsentLevel = (
  consentCookie: string,
  locale: ConsentLocale,
  returnUnrestrictedForUnknown?: boolean
): UserConsentLevel[] => {
  const REQUIRED_ONLY: UserConsentLevel[] = [UserConsentLevel.REQUIRED];
  const UNRESTRICTED: UserConsentLevel[] = [
    UserConsentLevel.REQUIRED,
    UserConsentLevel.FUNCTIONAL,
    UserConsentLevel.ADVERTISING,
  ];

  const consentLevelsSetByUser = getConsentPreferenceFromCookie(consentCookie);

  if (consentLevelsSetByUser.includes(UserConsentLevel.UNKNOWN)) {
    if (locale === ConsentLocale.CCPA) {
      return UNRESTRICTED;
    } else if (locale === ConsentLocale.GDPR) {
      return REQUIRED_ONLY;
    } else if (locale === ConsentLocale.NONE) {
      return UNRESTRICTED;
    } else if (returnUnrestrictedForUnknown) {
      return UNRESTRICTED;
    } else {
      return REQUIRED_ONLY;
    }
  }

  return consentLevelsSetByUser;
};

// Get consent level server-side from cookie sent in request
export const getConsentLevelFromRequest = (req: Request): UserConsentLevel[] => {
  return getConsentLevel(
    req.cookies?.[TRUST_ARC_PREFERENCE_COOKIE_NAME],
    getConsentLocale(req.cookies?.[TRUST_ARC_LOCALE])
  );
};

// Get consent level within the app from cookie stored in browser
// Optional boolean to default to unrestricted if a locale cannot be found.
export const getConsentLevelFromDocument = (
  returnUnrestrictedForUnknown: boolean
): UserConsentLevel[] => {
  return getConsentLevel(
    getCookieRaw(TRUST_ARC_PREFERENCE_COOKIE_NAME),
    getConsentLocale(getCookieRaw(TRUST_ARC_LOCALE)),
    returnUnrestrictedForUnknown
  );
};

export const getConsentLocaleFromDocument = () => {
  return getConsentLocale(getCookieRaw(TRUST_ARC_LOCALE));
};

// Get inner text content for DOM element (e.g., "Do not sell my info" or "Cookie Preferences")
export const modifyCookiePrefsLabelText = () => {
  let linkLabel = '';
  if (typeof window !== 'undefined' && window.truste?.eu?.prefButton) {
    linkLabel = window.truste?.eu?.prefButton?.innerText;
  }
  return linkLabel;
};

export const modifyCookiePrefsImgSrc = () => {
  let imgSrc = undefined;
  if (typeof window !== 'undefined' && window.truste?.eu?.prefButton) {
    imgSrc = window.truste.eu.prefButton.querySelector('img')?.src;
  }
  return imgSrc;
};

// Get onClick handler for cookie preferences DOM element
export const onOpenCookieConsentPrefs = () => {
  if (window.truste?.eu?.clickListener) {
    window.truste.eu.clickListener();
  }
};
